import React from 'react';
import { Card, Empty } from 'antd';
import {
  LineChart,
  Line,
  BarChart,
  Bar,
  XAxis,
  YAxis,
  CartesianGrid,
  Tooltip,
  Legend,
  ResponsiveContainer,
} from 'recharts';

interface RevenueChartProps {
  data: any[];
  type?: 'line' | 'bar';
  title: string;
  loading?: boolean;
}

const RevenueChart: React.FC<RevenueChartProps> = ({ 
  data, 
  type = 'bar', 
  title,
  loading = false 
}) => {
  // Transform data for chart
  const chartData = data.map(item => ({
    date: item._id.date,
    service: item._id.type === 'service' ? item.total : 0,
    recommend: item._id.type === 'recommend' ? item.total : 0,
  }));

  // Aggregate data by date
  const aggregatedData = chartData.reduce((acc: any[], curr) => {
    const existing = acc.find(item => item.date === curr.date);
    if (existing) {
      existing.service += curr.service;
      existing.recommend += curr.recommend;
    } else {
      acc.push({ ...curr });
    }
    return acc;
  }, []);

  // Sort by date
  aggregatedData.sort((a, b) => a.date.localeCompare(b.date));

  const formatCurrency = (value: number) => {
    return `${(value / 1000).toFixed(0)}K`;
  };

  if (!data || data.length === 0) {
    return (
      <Card title={title} loading={loading}>
        <Empty description="ບໍ່ມີຂໍ້ມູນ" />
      </Card>
    );
  }

  const ChartComponent = type === 'line' ? LineChart : BarChart;

  return (
    <Card title={title} loading={loading}>
      <ResponsiveContainer width="100%" height={300}>
        <ChartComponent data={aggregatedData}>
          <CartesianGrid strokeDasharray="3 3" />
          <XAxis 
            dataKey="date" 
            tick={{ fontSize: 12 }}
            angle={-45}
            textAnchor="end"
            height={80}
          />
          <YAxis tickFormatter={formatCurrency} />
          <Tooltip 
            formatter={(value: number) => `${value.toLocaleString()} ກີບ`}
            labelFormatter={(label) => `ວັນທີ່: ${label}`}
          />
          <Legend />
          {type === 'line' ? (
            <>
              <Line 
                type="monotone" 
                dataKey="service" 
                stroke="#1890ff" 
                name="ຄ່າບໍລິການ"
                strokeWidth={2}
              />
              <Line 
                type="monotone" 
                dataKey="recommend" 
                stroke="#52c41a" 
                name="ຄ່າແນະນຳ"
                strokeWidth={2}
              />
            </>
          ) : (
            <>
              <Bar dataKey="service" fill="#1890ff" name="ຄ່າບໍລິການ" />
              <Bar dataKey="recommend" fill="#52c41a" name="ຄ່າແນະນຳ" />
            </>
          )}
        </ChartComponent>
      </ResponsiveContainer>
    </Card>
  );
};

export default RevenueChart;